import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import useCustomStyles from "../../hooks/useCustomStyles";
import { accessCodesStyles } from "../../assets/styles/accessCodesStyles";
import { selectState } from "../../store/slices/themeSlice";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelectInput";
import { toast } from "react-toastify";
import { getBusinesses } from "../../store/actions/accessCodeActions";
import { sendInviteDispatch } from "../../api/awsApis";

type LanguageOption = {
  id: string;
  name: string;
};

type Recipient = {
  email: string;
  name: string;
};

const languageOptions: LanguageOption[] = [
  { id: "en", name: "English" },
  { id: "fr", name: "French" },
];

const parseRecipients = (rawText: string): Recipient[] => {
  return rawText
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      // Accept JSON line: {"name":"John","email":"john@company.com"}
      if (line.startsWith("{") && line.endsWith("}")) {
        try {
          const parsed = JSON.parse(line);
          return {
            name: String(parsed.name || "").trim(),
            email: String(parsed.email || "").trim(),
          };
        } catch {
          return { name: "", email: "" };
        }
      }

      // Accept CSV-ish line: Name, email@company.com
      const parts = line.split(",").map((item) => item.trim());
      if (parts.length >= 2) {
        const email = parts[parts.length - 1];
        const name = parts.slice(0, parts.length - 1).join(", ");
        return { name, email };
      }

      return { name: "", email: "" };
    })
    .filter((item) => item.name.length > 0 && item.email.length > 0);
};

function InviteDispatch() {
  const dispatch = useDispatch<AppDispatch>();
  const { businesses } = useSelector(
    (state: RootState) => state.accessCode_slice,
  );

  const { theme } = useSelector(selectState);
  const classes = useCustomStyles(accessCodesStyles, theme);

  const [companyCode, setCompanyCode] = useState<string>("");
  const [preferredLanguage, setPreferredLanguage] = useState<string>("en");
  const [inviteCode, setInviteCode] = useState<string>("");
  const [emailCode] = useState<string>("B00");
  const [recipientsText, setRecipientsText] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  React.useEffect(() => {
    if (!businesses || businesses.length === 0) {
      dispatch(getBusinesses({ type: "" }));
    }
  }, [businesses, dispatch]);

  const companyOptions = useMemo(
    () =>
      (businesses || [])
        .filter((business) => business?.name)
        .map((business) => ({ id: business.name, name: business.name })),
    [businesses],
  );

  const recipients = useMemo(
    () => parseRecipients(recipientsText),
    [recipientsText],
  );

  const canSend =
    companyCode.length > 0 &&
    preferredLanguage.length > 0 &&
    inviteCode.trim().length > 0 &&
    recipients.length > 0 &&
    !loading;

  const handleSend = async () => {
    if (!canSend) {
      toast.error(
        "Please fill all fields and add at least one valid recipient.",
      );
      return;
    }

    try {
      setLoading(true);

      const requests = recipients.map((recipient) =>
        sendInviteDispatch({
          email: recipient.email,
          name: recipient.name,
          emailCode,
          companyCode,
          preferredLanguage,
          extras: {
            inviteCode: inviteCode.trim(),
          },
        }),
      );

      const results = await Promise.allSettled(requests);
      const failed = results.filter((result) => result.status !== "fulfilled");

      const succeededCount = results.length - failed.length;
      if (failed.length === 0) {
        toast.success(`Invites sent successfully: ${succeededCount}`);
      } else {
        toast.error(
          `Sent ${succeededCount} invite(s), failed ${failed.length}. Please retry failed rows.`,
        );
      }
    } catch {
      toast.error("Failed to send invites.");
    } finally {
      setLoading(false);
    }
  };

  const width = window.innerWidth * 0.4; // Web equivalent for width and height

  return (
    <div className={classes.container}>
      <div className={classes.tableContainer}>
        <h2 className={classes.header}>INVITE DISPATCH</h2>
      </div>{" "}
      <div style={{ marginBottom: "40px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(2, 1fr)`, // Two equal columns
            gap: "16px",
            alignItems: "center",
            marginTop: "12px",
            alignContent: "center",
            width: width,
          }}
        >
          <CustomSelect
            label="Company"
            placeholder="Select company"
            name="companyCode"
            value={companyCode}
            options={companyOptions}
            onChange={(event) => setCompanyCode(event.target.value)}
          />
          <CustomSelect
            label="Language"
            placeholder="Select language"
            name="preferredLanguage"
            value={preferredLanguage}
            options={languageOptions}
            onChange={(event) => setPreferredLanguage(event.target.value)}
          />

          <CustomInput
            label="Email template code"
            name="emailCode"
            value={emailCode}
            disabled
            onChange={() => {}}
          />

          <CustomInput
            label="Invite code"
            name="inviteCode"
            value={inviteCode}
            placeholder="e.g. CnC9dttBqqNn8IKJzoUL"
            onChange={(event) => setInviteCode(event.target.value)}
          />
        </div>
        <div style={{ width, marginTop: "14px" }}>
          <label
            style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}
          >
            Recipients - one per line: Name, email@company.com
          </label>
          <textarea
            value={recipientsText}
            onChange={(event) => setRecipientsText(event.target.value)}
            placeholder={
              "Guston Alwanga, galwanga@elisyanwealth.com\nJane Smith, jane@company.com"
            }
            rows={8}
            style={{
              width: "100%",
              borderRadius: "8px",
              padding: "12px",
              border: "1px solid #4b4b4b",
              backgroundColor: "transparent",
              color: "inherit",
              resize: "vertical",
              boxSizing: "border-box",
            }}
          />
          <p style={{ marginTop: "8px", opacity: 0.8 }}>
            Parsed recipients: {recipients.length}
          </p>
        </div>
        <div className={classes.buttonContainer} style={{ marginTop: "14px" }}>
          <CustomButton
            title={"Send Invites"}
            handleClick={handleSend}
            name={"send-invites"}
            loading={loading}
            disabled={!canSend}
          />
        </div>
      </div>
    </div>
  );
}

export default InviteDispatch;
